import DOMPurify from 'dompurify';

import {openEmailLinksInNewTab} from '../../../utils/openEmailLinksInNewTab';
import {MAX_EMAIL_WIDTH} from '../constants/MAX_EMAIL_WIDTH';
import {collapseQuotedReplies} from './collapseQuotedReplies';

// We render the BE email inside our own document shell. We only ADD a wrapper element
// (#omnetic-fit) and shell-level styles for html/body/#omnetic-fit — the sanitized BE markup and
// its own <style> are preserved untouched (apart from web links, which are made to open in a new
// tab), so we never override the email's classes or elements.
// `width: max-content` makes the wrapper shrink to the content's natural width (capped at the design
// width), which is what lets us measure the email's real width under sandbox="allow-same-origin".
//
// Default email typography, set on the BODY with NO !important — emails that declare their own
// fonts/sizes keep them via the normal cascade; only content without a declaration inherits this
// default. It deliberately mirrors how mail clients render messages (Roboto 14px/20px, kerning on)
// instead of the app font, so emails read as email, not as app UI. Roboto is not bundled: on
// systems without it the stack falls back to the platform sans-serif. Size/line-height sit on body
// only (not html) so emails using rem keep 1rem=16px. Scoping is automatic: the iframe is an
// isolated document, so none of this leaks into the app.
const EMAIL_TYPOGRAPHY_STYLE =
  `:root{--font-sans:ui-sans-serif,system-ui,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";--font-mono:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace}` +
  `body{font-family:Roboto,sans-serif;font-size:14px;line-height:20px;font-weight:400;font-style:normal;font-feature-settings:"kern";font-variation-settings:var(--default-font-variation-settings,normal)}`;

// Collapsed quoted-reply toggle (see collapseQuotedReplies). The email iframe runs no scripts, so
// the toggle is a native <details>; these styles turn its <summary> into a Gmail-style "···" pill
// and hide the default disclosure marker across engines.
const QUOTE_TOGGLE_STYLE =
  `details.omnetic-quote{margin-top:8px}` +
  `details.omnetic-quote>summary{list-style:none;display:inline-flex;align-items:center;justify-content:center;width:36px;height:20px;border-radius:10px;background:#e8eaed;color:#5f6368;font-size:18px;line-height:1;cursor:pointer;user-select:none}` +
  `details.omnetic-quote>summary::-webkit-details-marker{display:none}` +
  `details.omnetic-quote>summary::marker{content:""}` +
  `details.omnetic-quote>summary:hover{background:#dadce0}`;

const SHELL_STYLE =
  `html,body{margin:0;padding:0;background:transparent}` +
  EMAIL_TYPOGRAPHY_STYLE +
  `#omnetic-fit{display:inline-block;width:max-content;max-width:${MAX_EMAIL_WIDTH}px}` +
  QUOTE_TOGGLE_STYLE;

export function buildThreadEmailDocument(htmlData: string | null, quotedLabel: string): string {
  const sanitized = DOMPurify.sanitize(htmlData ?? '', {WHOLE_DOCUMENT: true});
  const parsed = new DOMParser().parseFromString(sanitized, 'text/html');
  // DOMPurify drops `target`, and the iframe is sandboxed (see ThreadEmailWindow), so a plain link
  // click would try to load the site inside the frame and fail — force web links into a new tab.
  openEmailLinksInNewTab(parsed.body);
  const emailHead = parsed.head?.innerHTML ?? '';
  const emailBody = collapseQuotedReplies(parsed.body?.innerHTML ?? '', quotedLabel);

  return (
    `<!doctype html><html><head><meta charset="utf-8"><style>${SHELL_STYLE}</style>${emailHead}` +
    `</head><body><div id="omnetic-fit">${emailBody}</div></body></html>`
  );
}
